import React, { useEffect, useMemo, useState } from 'react';

import cx from 'classnames';
import { isMobile } from 'react-device-detect';

import { updatePriceAlertPreference } from 'actions/trade';
import SwitchButton from 'components/bellAlerts/partials/SwitchButton';
import Render from 'components/render';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import i18n from 'i18n/config';
import { tradePreferencesSelector } from 'selectors/otherSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { CrossIcon } from 'styles/assets/icons';
import BellIcon from 'styles/assets/icons/BellIcon';
import { Product } from 'types/IProducts';
import Box from 'UIKit/Box';
import IconButton from 'UIKit/IconButton';
import MaterialDialog from 'UIKit/MaterialDialog';

import styles from './priceAlert.module.scss';
import PriceAlertTag from './PriceAlertTag';

const PriceAlert = ({ product }: { product: Product }) => {
  const MODAL_PRICE_ALERT_HEADING = i18n.t('trading:priceAlertModalHeading', {
    symbol: product.symbol,
  });
  const MODAL_PRICE_ALERT_MESSAGE = i18n.t('trading:priceAlertModalMessage', {
    symbol: product.symbol,
  });
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isAlertChecked, setAlertChecked] = useState<boolean>(false);
  const { price_alert_assets: priceAlertAssets } = useAppSelector(
    tradePreferencesSelector
  );
  const dispatch = useAppDispatch();

  const showPriceAlertTag = useMemo(
    () =>
      !priceAlertAssets?.includes(
        product?.spot_index?.config?.underlying_asset as string
      ),
    [priceAlertAssets, product?.spot_index?.config?.underlying_asset]
  );

  const onClose = () => {
    setModalOpen(false);
  };

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    trackMixpanelEvent('Alert switch clicked', {
      is_mobile_app: isMobile,
      product_name: product.symbol,
      product_type: product.contract_type,
      switch: checked ? 'On' : 'Off',
    });

    try {
      const payload = {
        data: {
          alert: checked,
          underlying_asset: product?.spot_index?.config?.underlying_asset,
        },
      };
      await dispatch(updatePriceAlertPreference(payload));
      setAlertChecked(checked);
    } catch (err) {}
  };

  const handleIcon = () => {
    setModalOpen(true);
    trackMixpanelEvent('Alert icon clicked', {
      is_mobile_app: isMobile,
      product_name: product.symbol,
      product_type: product.contract_type,
    });
  };

  useEffect(() => {
    setAlertChecked(
      priceAlertAssets?.includes(product?.spot_index?.config?.underlying_asset as string)
    );
  }, [priceAlertAssets, product]);

  return (
    <Box className={styles.priceAlertContainer}>
      <IconButton
        data-testid="priceAlertIcon"
        className={cx(styles.bellIconContainer, {
          [styles.selectedBell]: isAlertChecked,
        })}
        icon={<BellIcon className={styles.bellIcon} />}
        onClick={handleIcon}
      />
      <MaterialDialog
        darkOverlay
        open={isModalOpen}
        contentClassName={styles.modalContainer}
        onClose={onClose}
        modalTestId="priceAlertModal"
      >
        <div className={styles.headingContainer}>
          <h1>{MODAL_PRICE_ALERT_HEADING}</h1>
          <Box as="span" data-testid="closeIcon" onClick={onClose}>
            <CrossIcon />
          </Box>
        </div>
        <Box
          flex="flex"
          justifyContent="between"
          alignItems="center"
          className={styles.detailsContainer}
        >
          <p>{MODAL_PRICE_ALERT_MESSAGE}</p>
          <SwitchButton
            buttonText=""
            onChange={handleToggle}
            data-testid="priceAlertSwitch"
            checked={isAlertChecked}
          />
        </Box>
      </MaterialDialog>
      <Render when={showPriceAlertTag}>
        <Box className={styles.tagBox}>
          <PriceAlertTag />
        </Box>
      </Render>
    </Box>
  );
};

export default PriceAlert;
